import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  mdiAccountOutline,
  mdiCameraPlus,
  mdiClose,
  mdiEmailOutline,
  mdiHelpCircleOutline,
  mdiMapMarkerOutline,
  mdiPhoneOutline
} from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";
import { useToggle } from "usehooks-ts";

import Button from "components/Button";
import ConfirmationModal from "components/ConfirmationModal";
import Header from "components/Header";
import { ArrowBackSVGIcon } from "components/SVGIcons";
import { routes } from "constants/routes";
import { INTEGRATIONS_SETTINGS_ITEM, USER_SETTINGS_LIST } from "constants/user-settings";
import { formatPhoneNumber } from "helpers/formatPhoneNumber";
import { useColorScheme } from "hooks/useColorScheme";
import useDeviceType from "hooks/useDeviceType";
import { useIntegrations } from "hooks/useIntegrations";
import { useUserFeature } from "hooks/useUserFeature";
import MainLayout from "layouts/Main";
import { UserAccount } from "models/User";
import { AppDispatch, RootState } from "store";
import { logOut } from "store/auth";
import { selectStripeSubscribeStatus } from "store/userSetting/selector";
import UserSettingItem from "./modules/UserSettingItem";
import styles from "./style.module.scss";
import { useAnalytics } from "hooks/useAnalytics";

export default function UserSettings() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [isConfirmModalOpen, toggleConfirmModal] = useToggle();
  const { currentIsDark } = useColorScheme();
  const { isDesktop } = useDeviceType();
  const { userDetails } = useSelector((state: RootState) => state.userState);
  const { trackAllEvents } = useAnalytics();
  const [avatar, setAvatar] = useState<File>();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setAvatar(e.target.files[0]);
    }
  };

  const { email, contact_name, contact_phone, address_object } = useMemo<
    Partial<UserAccount>
  >(() => {
    if (userDetails) return userDetails;
    else {
      return {};
    }
  }, [userDetails]);

  const address = [
    address_object?.address,
    address_object?.city,
    address_object?.state,
    address_object?.country
  ]
    .filter((item) => !!item && item !== "undefined")
    .join(", ");

  const { isCarrierSubscribed, isBrokerSubscribed, isCarrierTrial, isFactoringApproved } =
    useUserFeature();
  const { integrations } = useIntegrations();
  const stripeSubscriptionStatus = useSelector(selectStripeSubscribeStatus);
  const [showFullAccessButton, setShowFullAccessButton] = useState(
    isFactoringApproved !== "approved" && !stripeSubscriptionStatus.info
  );

  const goToHelp = () => {
    navigate(routes.SETTINGS_HELP);
  };

  const goToUserAccount = () => {
    navigate(routes.SETTINGS_ACCOUNT);
  };

  const goToBilling = () => {
    navigate(routes.SETTINGS_BILLING);
  };

  function userLogOut() {
    toggleConfirmModal();
    dispatch(logOut());
    trackAllEvents("home_other_logout");
  }

  return (
    <MainLayout
      desktopTitle="Settings"
      customHeader={
        <Header
          label=""
          goBackPath={routes.HOME}
          onClickIconButton={goToHelp}
          {...(isCarrierTrial || isCarrierSubscribed || isBrokerSubscribed
            ? { headerIcon: <Icon path={mdiHelpCircleOutline} /> }
            : { headerIcon: " " })}
          isHideDesktop
        />
      }
      hideNavigation
      className={styles.userSettingsContainer}>
      {showFullAccessButton && (
        <Button
          type="button"
          variant="outline"
          className={styles.fullAccessLabel}
          onClick={goToBilling}>
          <span>Upgrade to Full Access</span>
        </Button>
      )}
      <div className={styles.list}>
        {!isDesktop && (
          <button className={styles.accountButton} onClick={goToUserAccount}>
            <div className={styles.body}>
              <div className={styles.avatar}>
                <Icon path={mdiAccountOutline} className="m-auto" />
              </div>
              <div className={styles.content}>
                <h3 className={styles.email}>{contact_name}</h3>
                <span className="text-xs text-placeholder">Account</span>
              </div>
            </div>
            <ArrowBackSVGIcon />
          </button>
        )}
        <div className={styles.profileGridCard}>
          {isDesktop && (
            <div
              className={clsx(
                styles.profileCard,
                !currentIsDark && "drop-shadow-[0_4px_4px_#B5B5B533]"
              )}>
              <div className={styles.avatarWrapper}>
                {avatar ? (
                  <img
                    src={URL.createObjectURL(avatar)}
                    alt="avatar"
                    className="m-auto rounded-full"
                  />
                ) : (
                  <Icon path={mdiAccountOutline} className="m-auto size-14" />
                )}
                <label className={styles.addAvatar}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    name="avatar"
                    hidden
                  />
                  <Icon path={mdiCameraPlus} className="size-3.5" />
                </label>
              </div>

              <div className={styles.cardBody}>
                <div className={styles.header}>
                  <div>
                    <h3 className={styles.contactName}>{contact_name}</h3>
                    <span className="font-medium text-subtitle">Account</span>
                  </div>
                </div>

                <div className={styles.description}>
                  <div className={styles.detail}>
                    <Icon path={mdiMapMarkerOutline} className={styles.icon} />
                    <span className="text-nowrap">{address}</span>
                  </div>
                  <div className={styles.detail}>
                    <Icon path={mdiPhoneOutline} className={styles.icon} />
                    <span className="text-nowrap">
                      {formatPhoneNumber(typeof contact_phone === "string" ? contact_phone : "")}
                    </span>
                  </div>
                  <div className={styles.detail}>
                    <Icon path={mdiEmailOutline} className={styles.icon} />
                    <span className="text-nowrap">{email}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {USER_SETTINGS_LIST.map((item) => (
          <UserSettingItem key={item.id} data={item} />
        ))}
        {integrations.length > 0 && <UserSettingItem data={INTEGRATIONS_SETTINGS_ITEM} />}
      </div>

      <div className={styles.actionButtonWrapper}>
        {showFullAccessButton ? (
          <button className={styles.fullAccessButton} onClick={goToBilling}>
            <button
              onClick={(event) => {
                event.stopPropagation();
                setShowFullAccessButton(false);
              }}>
              <Icon path={mdiClose} className="absolute w-4 right-2 top-2" />
            </button>

            <span>Upgrade to Full Access</span>
          </button>
        ) : null}
        <Button size="lg" className={styles.logoutButton} rounded onClick={toggleConfirmModal}>
          LOGOUT
        </Button>
      </div>

      <ConfirmationModal
        isConfirmModalOpen={isConfirmModalOpen}
        toggleConfirmModal={toggleConfirmModal}
        labelCancelButton="Logout"
        labelOKButton="Cancel"
        onCancel={userLogOut}
        onOK={toggleConfirmModal}
        content="Are you sure you want to log out?"
      />
    </MainLayout>
  );
}
